import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Button,
  Typography,
  TextField,
  Box,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import BlockIcon from "@mui/icons-material/Block";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import Grid from "@mui/material/Grid2";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowId,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import DoDisturbAltIcon from "@mui/icons-material/DoDisturbAlt";
import PreviewPanel from "../helpers/components/PreviewPanel";
import {
  setFilteringDefJSONOutput,
  setIsValidRowFiltering,
  setRowFilteringDataForPayload,
} from "../../../../../features/uniqueDefSlice";
import { useDispatch } from "react-redux";
import { useFilterObjValidation } from "../helpers/hooks/useFilteringObjValidation";
import { useSelector } from "react-redux";
import { buildRowFilteringPayload } from "../helpers/functions/payloadBuilder";
interface FilterRow {
  id: number;
  fieldName: string;
  value: string;
  condition: string;
}

const DuplicateDefinitionRowFiltering = forwardRef(
  ({ setShowLookupFields }: any, ref) => {
    const { initialDuplicateData } = useSelector(
      (state: any) => state.uniqueDefinition,
    );
    const dispatch = useDispatch();
    const apiRef = React.useRef<any>(null);
    const [activityDate, setActivityDate] = useState({
      field: "",
      rawTableFormat: "",
      additionalRawTableFormat: "",
      filteringFormat: "",
    });
    const [filterRows, setFilterRows] = useState<FilterRow[]>([
      { id: 1, fieldName: "does_exist", value: "", condition: "" },
    ]);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
      if (!initialDuplicateData?.filtering_definition || isInitialized) return;

      try {
        const filteringDef = JSON.parse(
          initialDuplicateData.filtering_definition,
        );

        /* -----------------------------
       1. Normalize ACTIVITY DATE
       ----------------------------- */
        const ad = filteringDef.activity_date ?? {};
        const rawFormats = Array.isArray(ad.raw_table_format)
          ? ad.raw_table_format
          : [];

        setActivityDate({
          field: ad.field ?? "",
          rawTableFormat: rawFormats[0] ?? "",
          additionalRawTableFormat: rawFormats[1] ?? "",
          filteringFormat: ad.filtering_format ?? "",
        });

        /* -----------------------------
       2. Normalize OTHER FILTERS
       ----------------------------- */
        const rows: FilterRow[] = [];
        let rowId = 1;

        Object.entries(filteringDef).forEach(([key, value]: any) => {
          if (key === "activity_date") return;

          rows.push({
            id: rowId++,
            fieldName: key,
            value: Array.isArray(value?.value)
              ? value.value.join(", ")
              : (value?.value ?? ""),
            condition: value?.condition
              ? value.condition
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c: string) => c.toUpperCase())
              : "",
          });
        });

        setFilterRows(
          rows.length
            ? rows
            : [{ id: 1, fieldName: "does_exist", value: "", condition: "" }],
        );

        setIsInitialized(true);
      } catch (err) {
        console.error("Invalid filtering_definition JSON:", err);
      }
    }, [initialDuplicateData, isInitialized]);

    const conditionOptions = [
      "Equal",
      "Not Equal",
      "Contains",
      "Not Contain",
      "Greater Than",
      "Less Than",
    ];

    // map condition -> icon component
    const conditionIconMap: Record<string, JSX.Element> = {
      Equal: <DragHandleIcon fontSize="small" sx={{ color: "#4caf50" }} />,
      "Not Equal": (
        <DoDisturbAltIcon fontSize="small" sx={{ color: "#f44336" }} />
      ),
      Contains: <FormatQuoteIcon fontSize="small" sx={{ color: "#2196f3" }} />,
      "Not Contain": <BlockIcon fontSize="small" sx={{ color: "#ff9800" }} />,
      "Greater Than": (
        <TrendingUpIcon fontSize="small" sx={{ color: "#2e7d32" }} />
      ),
      "Less Than": (
        <TrendingDownIcon fontSize="small" sx={{ color: "#c62828" }} />
      ),
    };

    const addFilterRow = () => {
      const newId = Math.max(...filterRows.map((row) => row.id), 0) + 1; // Added fallback for empty array
      const newRow: FilterRow = {
        id: newId,
        fieldName: "",
        value: "",
        condition: "",
      };
      setFilterRows((prev) => [...prev, newRow]);
    };

    const removeFilterRow = (id: number | string) => {
      const numericId = Number(id); // coerce to number
      if (Number.isNaN(numericId)) {
        // guard: if id is invalid, do nothing (or log)
        console.warn("removeFilterRow called with invalid id:", id);
        return;
      }
      const row = filterRows.find((r) => r.id === numericId);
      if (row?.fieldName === "does_exist") {
        console.warn("Attempt to remove protected row 'does_exist' prevented.");
        return;
      }
      setFilterRows((prev) => prev.filter((row) => row.id !== numericId));
    };

    // inside your component, before columns definition
    const EditConditionSelect = (props: any) => {
      const { id, field, value, api } = props;
      const [open, setOpen] = useState(false);
      const current = String(value ?? "");

      useEffect(() => {
        // open after mount — small timeout helps in some MUI versions
        const t = setTimeout(() => setOpen(true), 0);
        return () => clearTimeout(t);
      }, []);

      const handleChange = (e: any) => {
        const newVal = e.target.value;
        // set edit buffer
        api.setEditCellValue({ id, field, value: newVal }, e);
        // commit and stop edit mode (closes editor)
        api.stopCellEditMode({ id, field });
      };

      return (
        <Select
          value={current}
          open={open}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          autoFocus
          IconComponent={ArrowDropDownIcon}
          onChange={handleChange}
          size="small"
          sx={{
            width: "100%",
            // 👇 removes the default outline + radius
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            "&.MuiOutlinedInput-root": {
              borderRadius: 0,
            },
          }}
        >
          {conditionOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {conditionIconMap[opt]}
                <Typography variant="body2">{opt}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      );
    };

    // then use it as the renderEditCell:
    const renderConditionEditCell = (params: any) => {
      return <EditConditionSelect {...params} />;
    };

    const columns: GridColDef[] = [
      {
        field: "fieldName",
        headerName: "Field Name In Table",
        flex: 2,
        editable: true,
        renderCell: (params: any) => {
          const value = params.value || "";
          return (
            <Typography
              variant="body2"
              sx={{
                color: value ? "text.primary" : "text.disabled",
                fontStyle: value,
                lineHeight: 1.5,
                py: 1,
              }}
            >
              {value || "Set field name in table"}
            </Typography>
          );
        },
      },
      {
        field: "value",
        headerName: "Value",
        flex: 1,
        editable: true,
        renderCell: (params: any) => {
          const value = params.value || "";
          return (
            <Typography
              variant="body2"
              sx={{
                color: value ? "text.primary" : "text.disabled",
                fontStyle: value,
                lineHeight: 1.5,
                py: 1,
              }}
            >
              {value || "Set filtering value"}
            </Typography>
          );
        },
      },
      {
        field: "condition",
        headerName: "Condition",
        flex: 1,
        editable: true,
        type: "singleSelect",
        valueOptions: conditionOptions,
        // render an icon + label for the cell display
        renderCell: (params: any) => {
          const val = String(params.value ?? "");
          const Icon = conditionIconMap[val];
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                cursor: "pointer",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {val && val ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {Icon}
                      <Typography variant="body2" sx={{ lineHeight: 1 }}>
                        {val}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: val ? "text.primary" : "text.disabled",
                      fontStyle: val,
                      lineHeight: 1.5,
                      py: 1,
                    }}
                  >
                    {"Set filtering condition"}
                  </Typography>
                )}
              </Box>
              <ArrowDropDownIcon
                fontSize="small"
                sx={{ color: "text.disabled" }}
              />
            </Box>
          );
        },
        renderEditCell: renderConditionEditCell,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 80,
        getActions: ({ id }: { id: GridRowId }) => [
          <GridActionsCellItem
            key={`delete-${id}`}
            icon={<DeleteIcon color="secondary" />}
            label="Delete"
            onClick={() => removeFilterRow(id)} // pass id (coerced inside removeFilterRow)
            showInMenu={false}
          />,
        ],
      },
    ];
    const processRowUpdate = (newRow: any) => {
      const updated: FilterRow = {
        id: Number(newRow.id),
        fieldName: newRow.fieldName || "", // Keep original value if it exists
        value: newRow.value || "", // Keep original value if it exists
        condition: newRow.condition || "", // Default to "Equal" if empty
      };

      setFilterRows((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
      return updated;
    };

    const handleProcessRowUpdateError = (error: any) => {
      console.error("Row update error:", error);
    };

    const previewObject = useMemo(() => {
      const filteringDef: Record<string, any> = {};

      const rawFormats = [
        activityDate.rawTableFormat,
        activityDate.additionalRawTableFormat,
      ]
        .map((f) => String(f ?? "").trim())
        .filter(Boolean);

      filteringDef["activity_date"] = {
        field: activityDate.field,
        raw_table_format: rawFormats,
        ...(activityDate.filteringFormat && {
          filtering_format: activityDate.filteringFormat,
        }),
      };

      // 🚫 DO NOT TOUCH THIS — as instructed
      for (const row of filterRows) {
        const key = row.fieldName || "";
        filteringDef[key] = {
          value:
            typeof row.value === "string" && row.value.length
              ? [row.value.trim()]
              : [],
          condition: (row.condition || "").toLowerCase().replace(/\s+/g, "_"),
        };
      }

      return filteringDef;
    }, [activityDate, filterRows]);

    const previewObjectForOnlyValidationPurpose = useMemo(() => {
      const obj: Record<string, any> = {};
      const rawFormats: any[] = [];
      if (activityDate.rawTableFormat.trim()) {
        rawFormats.push({
          raw_table_format: activityDate.rawTableFormat.trim(),
        });
      }
      if (activityDate.additionalRawTableFormat.trim()) {
        rawFormats.push({
          additional_raw_table_format:
            activityDate.additionalRawTableFormat.trim(),
        });
      }
      obj["activity_date"] = {
        field: activityDate.field,
        raw_table_format: rawFormats,
        ...(activityDate.filteringFormat && {
          filtering_format: activityDate.filteringFormat,
        }),
      };
      for (const row of filterRows) {
        const key = row.fieldName || "";
        obj[key] = {
          value:
            typeof row.value === "string" && row.value.length
              ? [row.value.trim()]
              : [],
          condition: (row.condition || "").toLowerCase().replace(/\s+/g, "_"),
        };
      }
      return obj;
    }, [activityDate, filterRows]);

    // dispatch only when previewObject (or previewJSON) changes
    const [, setDisplayedPreview] = useState<any>({});
    const [displayedPreviewJSON, setDisplayedPreviewJSON] = useState<string>(
      () => JSON.stringify({}, null, 2), // -> "{}"
    );

    useEffect(() => {
      if (isInitialized) {
        handleRefreshPreview();
      }
      // eslint-disable-next-line
    }, [isInitialized]);

    // IMPORTANT: we only dispatch when the user clicks Refresh
    const handleRefreshPreview = () => {
      const payload = buildRowFilteringPayload(activityDate, filterRows);
      setDisplayedPreview(previewObject);
      setDisplayedPreviewJSON(JSON.stringify(previewObject, null, 2));
      dispatch(setFilteringDefJSONOutput(previewObject));
      dispatch(setRowFilteringDataForPayload(payload));
    };
    const refiltering = () => {
      handleRefreshPreview();
      setShowLookupFields(true);
    };
    const handleCellClick = (params: any) => {
      try {
        const isEditable = Boolean(params.isEditable);
        const getCellMode = apiRef.current?.getCellMode;
        const startEdit = apiRef.current?.startCellEditMode;
        if (!isEditable || !getCellMode || !startEdit) return;

        if (getCellMode(params.id, params.field) === "view") {
          startEdit({ id: params.id, field: params.field });
        }
      } catch (err) {
        console.warn("startCellEditMode error:", err);
      }
    };

    const validationResult = useFilterObjValidation(
      previewObjectForOnlyValidationPurpose,
    );

    dispatch(setIsValidRowFiltering(validationResult.valid));
    useImperativeHandle(ref, () => ({
      refresh: () => {
        const payload = buildRowFilteringPayload(activityDate, filterRows);
        dispatch(setRowFilteringDataForPayload(payload));
        dispatch(setFilteringDefJSONOutput(previewObject));
        return payload;
      },
    }));

    return (
      <Box>
        <Grid container spacing={3} sx={{ p: 2, mb: 2 }}>
          {/* Left Column - Row Filtering */}
          <Grid size={12}>
            <Box sx={{ height: "fit-content" }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Row Filtering
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Activity Date
              </Typography>

              {/* Activity Date Field */}
              <Box
                sx={{
                  mb: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  // width: "70%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    "& .MuiFormHelperText-root": { mt: "3px" },
                  }}
                >
                  <TextField
                    helperText="Name of relevant date field in raw table"
                    sx={{ width: "30%" }}
                    label="Field"
                    value={activityDate.field}
                    color="secondary"
                    onChange={(e) =>
                      setActivityDate((p) => ({ ...p, field: e.target.value }))
                    }
                    size="small"
                    required
                  />

                  <TextField
                    sx={{ width: "30%" }}
                    helperText="Format of value in said field"
                    label="Raw Table Format"
                    color="secondary"
                    value={activityDate.rawTableFormat}
                    onChange={(e) =>
                      setActivityDate((p) => ({
                        ...p,
                        rawTableFormat: e.target.value,
                      }))
                    }
                    size="small"
                    required
                  />
                </Box>
                <Typography variant="caption">
                  All fields are mandatory
                </Typography>
                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                        margin: 0,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ExpandMoreIcon />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "normal", fontSize: 15 }}
                      >
                        Optional Fields
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      gap: 2,
                      display: "flex",
                      flexDirection: "column",
                      ml: 3,
                    }}
                  >
                    <TextField
                      sx={{ width: "40%" }}
                      color="secondary"
                      label="Additional Raw Table Format"
                      value={activityDate.additionalRawTableFormat}
                      onChange={(e) =>
                        setActivityDate((p) => ({
                          ...p,
                          additionalRawTableFormat: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                    />

                    <TextField
                      sx={{ width: "40%" }}
                      color="secondary"
                      label="Filtering Format"
                      value={activityDate.filteringFormat}
                      onChange={(e) =>
                        setActivityDate((p) => ({
                          ...p,
                          filteringFormat: e.target.value,
                        }))
                      }
                      size="small"
                      fullWidth
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* Other Settings */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Other Settings
              </Typography>

              <Box sx={{ width: "100%", mb: 3 }}>
                <DataGrid
                  density="compact"
                  rows={filterRows}
                  columns={columns}
                  processRowUpdate={processRowUpdate}
                  onProcessRowUpdateError={handleProcessRowUpdateError}
                  apiRef={apiRef}
                  hideFooter
                  disableRowSelectionOnClick
                  disableColumnMenu
                  onCellClick={handleCellClick}
                  isCellEditable={(params) => {
                    if (
                      params.field === "fieldName" &&
                      params.row.fieldName === "does_exist"
                    ) {
                      return false;
                    }
                    return true;
                  }}
                  sx={{
                    border: "none",
                    ".MuiDataGrid-columnHeaders": {
                      borderBottom: "none",
                    },
                    ".MuiDataGrid-cell": {
                      borderBottom: "none",
                    },

                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f5f5f5",
                      border: "none",
                    },
                    "& .MuiDataGrid-root": {
                      border: "1px solid #e0e0e0",
                    },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline: "none !important",
                      },
                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline: "none !important",
                      },

                    // 👇 highlight editable cell in edit mode
                    "& .MuiDataGrid-cell--editing": {
                      border: (theme) =>
                        `2px solid ${theme.palette.secondary.main}`,
                    },
                  }}
                />
              </Box>

              {/* Add Row Button */}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addFilterRow}
                sx={{ textTransform: "none" }}
                color="secondary"
                data-tracker="388"
              >
                ADD ROW
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Accordion sx={{ ml: 2 }}>
            <AccordionSummary
              sx={{
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  margin: 0,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ExpandMoreIcon />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "normal", fontSize: 15 }}
                >
                  Preview
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <PreviewPanel
                json={displayedPreviewJSON}
                onRefresh={handleRefreshPreview}
                title=""
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
        {/* Bottom Action Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 3,
            mb: 2,
            p: 2,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<FilterAltIcon />}
            onClick={refiltering}
            color="secondary"
            disabled={!validationResult.valid}
            data-tracker="387"
          >
            REFILTER ROWS
          </Button>
        </Box>
      </Box>
    );
  },
);

export default DuplicateDefinitionRowFiltering;
